import { useEffect, useId, useRef, type ReactNode } from 'react';
import { HbsButton } from './Button';

interface HbsModalProps {
  /** DOM id for the dialog. Its primary action becomes `<id>-primary`, so one
   *  prop gives tests a stable handle on both. */
  id?: string;
  /** When false, renders nothing. */
  open: boolean;
  /** Called on Esc or backdrop click. Not auto-fired by primaryAction. */
  onClose: () => void;
  /** Header text; also the dialog's accessible name. */
  title: string;
  /** Modal body. */
  children: ReactNode;
  /** Optional primary button rendered in the footer. */
  primaryAction?: { label: string; onClick: () => void };
  /** Size variant. Default 'sm'. */
  size?: 'sm' | 'md';
  /** Default true. */
  dismissOnBackdrop?: boolean;
  /** Default true. */
  dismissOnEsc?: boolean;
}

export function HbsModal({
  id,
  open,
  onClose,
  title,
  children,
  primaryAction,
  size = 'sm',
  dismissOnBackdrop = true,
  dismissOnEsc = true,
}: HbsModalProps): JSX.Element | null {
  const titleId = useId();
  const primaryButtonRef = useRef<HTMLButtonElement | null>(null);

  // Esc dismissal
  useEffect(() => {
    if (!open || !dismissOnEsc) return;
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, dismissOnEsc, onClose]);

  // Focus management: move focus to primary button on open, restore on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    primaryButtonRef.current?.focus();
    return () => {
      previouslyFocused?.focus();
    };
  }, [open]);

  // Body scroll lock: capture prior value, restore on unmount/close.
  useEffect(() => {
    if (!open) return;
    const prior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prior;
    };
  }, [open]);

  if (!open) return null;

  const maxWidth = size === 'md' ? 'max-w-md' : 'max-w-sm';

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={() => {
        if (dismissOnBackdrop) onClose();
      }}
    >
      <div
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className={`bg-hbs-surface rounded-hbs-xl border border-hbs-border shadow-hbs-lg p-6 w-full ${maxWidth}`}
      >
        <h2
          id={titleId}
          className="font-display text-[18px] tracking-hbs-tight text-hbs-text mb-3"
        >
          {title}
        </h2>
        <div className="text-[14px] text-hbs-text leading-relaxed space-y-3">
          {children}
        </div>
        {primaryAction && (
          <div className="mt-6 flex justify-end">
            <HbsButton
              id={id ? `${id}-primary` : undefined}
              onClick={primaryAction.onClick}
              ref={primaryButtonRef}
            >
              {primaryAction.label}
            </HbsButton>
          </div>
        )}
      </div>
    </div>
  );
}
